import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Loader2, Baby, Sparkles, User, GraduationCap, Share2 } from "lucide-react";
import { toast } from "sonner";
import { getExplain, getVerseExplain } from "../api";
import { APP_NAME } from "../lib/appLinks";
import { tourActive } from "../lib/tour";

const LEVELS = [
  { key: "child", label: "Child", sub: "Age 7", icon: Baby },
  { key: "teen", label: "Teen", sub: "Age 15", icon: Sparkles },
  { key: "adult", label: "Adult", sub: "General", icon: User },
  { key: "scholar", label: "Scholar", sub: "In depth", icon: GraduationCap },
];

export default function ExplainPanel({ open, onOpenChange, book, chapter, bookName, verse, verseText }) {
  const [levels, setLevels] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [active, setActive] = useState("adult");

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLevels(null); setError(false); setLoading(true); setActive("adult");
    const req = verse ? getVerseExplain(book, chapter, verse) : getExplain(book, chapter);
    req
      .then((d) => { if (alive) setLevels(d.levels || {}); })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, book, chapter, verse]);

  const text = levels?.[active];
  const ref = verse ? `${bookName} ${chapter}:${verse}` : `${bookName} ${chapter}`;
  const levelLabel = LEVELS.find((l) => l.key === active)?.label || "";

  const handleShare = async () => {
    if (!text) return;
    const dl = `${window.location.origin}/download`;
    const quote = verse && verseText ? `\u201C${verseText}\u201D\n\n` : "";
    const body = `Explain \u2014 ${ref}\n\n${quote}${levelLabel} explanation:\n${text}\n\nRead the Bible free on ${APP_NAME}:\n${dl}`;
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ title: `Explain \u2014 ${ref}`, text: body });
        return;
      }
    } catch (e) {
      if (e && e.name === "AbortError") return;
    }
    try {
      await navigator.clipboard.writeText(body);
      toast("Explanation copied — paste it anywhere");
    } catch {
      toast("Couldn’t share right now");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="font-ui w-full max-w-md overflow-y-auto"
        data-testid="explain-panel"
        onPointerDownOutside={(e) => { if (tourActive()) e.preventDefault(); }}
        onInteractOutside={(e) => { if (tourActive()) e.preventDefault(); }}
      >
        <SheetHeader>
          <SheetTitle className="font-serif-display text-2xl">Explain · {ref}</SheetTitle>
          <SheetDescription className="sr-only">
            {verse ? "Plain-language explanation of this verse at four reading levels" : "Plain-language explanations of this chapter at four reading levels"}
          </SheetDescription>
        </SheetHeader>

        {verse && verseText && (
          <div className="mt-4 rounded-2xl border border-primary/20 bg-primary/5 p-4" data-testid="explain-verse-text">
            <p className="reading-text text-sm italic leading-relaxed text-foreground/80">
              <span className="font-ui not-italic font-bold text-primary">{verse}</span> {verseText}
            </p>
          </div>
        )}

        {loading ? (
          <div className="mt-10 flex flex-col items-center gap-3 text-center" data-testid="explain-loading">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Preparing explanations…<br />(first time takes a few seconds)</p>
          </div>
        ) : error ? (
          <p className="mt-8 text-sm text-muted-foreground">Couldn’t load explanations right now. Please try again shortly.</p>
        ) : levels ? (
          <div className="mt-6">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="explain-levels">
              {LEVELS.map((l) => {
                const on = active === l.key;
                return (
                  <button
                    key={l.key}
                    onClick={() => setActive(l.key)}
                    data-testid={`explain-level-${l.key}`}
                    className={`flex flex-col items-center gap-1 rounded-2xl border px-2 py-3 text-center transition-all ${
                      on ? "border-primary bg-primary/15 text-primary" : "border-border bg-background/40 text-muted-foreground hover:border-primary/40"
                    }`}
                  >
                    <l.icon className="h-4 w-4" />
                    <span className="text-xs font-semibold">{l.label}</span>
                    <span className="text-[10px] opacity-70">{l.sub}</span>
                  </button>
                );
              })}
            </div>
            <div
              key={active}
              data-testid="explain-content"
              className="mt-6 animate-in fade-in slide-in-from-bottom-2 rounded-3xl border border-border bg-background/50 p-6 backdrop-blur-md"
            >
              <p className="reading-text text-base leading-relaxed text-foreground/90">{text}</p>
            </div>
            <button
              onClick={handleShare}
              data-testid="explain-share"
              className="font-ui mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full border border-border bg-background/40 py-3 text-sm font-semibold text-foreground/80 transition-colors hover:bg-muted"
            >
              <Share2 className="h-4 w-4" /> Share this explanation
            </button>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
